/**
 * Idempotent create submission (SPEC-48 Fix F).
 *
 * Both job-creation forms (dispatcher JobForm, office OfficeJobForm) POST
 * /api/v1/jobs, which is now behind the server's idempotent() middleware. The
 * server can only dedupe what the client replays with the SAME key, so the key
 * has to outlive a single fetch attempt — and that is the whole design problem:
 * a UUID minted inside the submit function is a fresh intent every time, which
 * is exactly the double-create this fix exists to kill.
 *
 * The contract, copied from the punch/finish flows already in this app:
 *   1. mint the key ONCE per logical submission (held in a ref across renders);
 *   2. replay that same key on retry — the server returns its cached 201
 *      instead of creating a second job;
 *   3. clear the key when the user EDITS a field, because an edited payload is
 *      a NEW intent that must create a new job;
 *   4. clear the key after a confirmed success, so the next submission starts
 *      fresh.
 *
 * Why a plain function instead of a hook: the forms keep their own
 * useState/useRef shape today, and a hook would mean restructuring ~400 lines
 * of JSX for two call sites. This keeps the state where it already lives (the
 * form's ref) and makes the four rules above one importable, testable routine —
 * the same reasoning that put `launchGatePunch` and `classifyReplayOutcome`
 * beside the flows they serve, and the reason `fetcher` is injectable: this
 * repo has no DOM test harness, so the logic has to be exercisable without one.
 */

import { apiFetch } from "./api";

/** Where the key lives. Structurally compatible with `useRef` so a form can
 * hand over its own ref without a wrapper. */
export interface KeyHolder {
  current: string | null;
}

/** The fetch seam. Defaults to the production `apiFetch`; tests inject a stub
 * that records the header and can fail on demand. */
export type CreateFetcher = <T>(
  path: string,
  options?: RequestInit,
) => Promise<T>;

export type IdempotentCreateResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: unknown };

/**
 * POST `path` with a stable idempotency key held in `keyRef`.
 *
 * - First call: mints the key, sends it, returns the parsed JSON on 2xx.
 * - Failure of ANY kind: the key SURVIVES in the ref (the whole point — a
 *   lost-201 retry must replay the same key).
 * - Success: the key is cleared, so the next submission is a fresh intent.
 *
 * Why the key also survives a 4xx: the server caches 2xx responses only, so a
 * replay after a validation/gate failure is re-evaluated rather than replayed —
 * safe. And a user who corrects a form edits a field, which clears the key via
 * `invalidateOnEdit` anyway. Two rules (clear on success, clear on edit) beat
 * a third error-dependent branch that changes no observable outcome.
 */
export async function idempotentCreate<T>(
  path: string,
  body: unknown,
  keyRef: KeyHolder,
  fetcher: CreateFetcher = apiFetch,
): Promise<IdempotentCreateResult<T>> {
  if (keyRef.current === null) {
    keyRef.current = crypto.randomUUID();
  }
  const key = keyRef.current;

  try {
    const data = await fetcher<T>(path, {
      method: "POST",
      headers: { "Idempotency-Key": key },
      body: JSON.stringify(body),
    });
    // Landed. Drop the key: the next submission is a new intent.
    keyRef.current = null;
    return { ok: true, data };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * The edit rule, as a pure function so it is testable without a DOM: "clear
 * the key when a field changes" is the part of this fix that is invisible in
 * the JSX and easy to get subtly wrong — clear on success but not on edit, and
 * an edited resubmit would silently replay a cached 201 for a DIFFERENT payload
 * instead of creating the job the dispatcher is looking at.
 */
export function invalidateOnEdit(keyRef: KeyHolder): void {
  keyRef.current = null;
}
